import {
  IMarketingCampaignEntry,
  ITreatmentStep,
  ITreatmentStepScopeData,
  isContactActor,
  isContactReferrer,
  isPatientReferrer,
  isStafferActor,
  type IAppointment,
  type IAppointmentScopeData,
  type ICandidateCalendarEvent,
  type IContactDetails,
  type IGapScopeData,
  type IInvoice,
  type IInvoiceScopeData,
  type IPatient,
  type IPatientScopeData,
  type IPractice,
  type IPracticeScopeData,
  type IReferral,
  type IReferralScopeData,
  type ITreatmentPlan,
  type ITreatmentPlanScopeData,
  type ReferralActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  snapshot,
  undeletedQuery,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { generateSelfConfirmUrl } from '../models/appointment/patient-confirm-appointment';
import { Brand } from '../models/brand';
import { generateGapOfferUrl } from '../models/gap/patient-confirm-gap-offer';
import { Invoice } from '../models/invoice/invoice';
import { generateViewInvoiceUrl } from '../models/invoice/patient-view-invoice';
import {
  generateAppointmentBookingUrl,
  generateMedicalHistoryUrl,
  generatePatientFormsUrl,
  generateUnsubscribeUrl,
} from '../models/patient/generate-patient-portal-links';
import { Patient } from '../models/patient/patient';
import { OrganisationCache } from '../models/organisation/organisation-cache';

export class ScopeDataBuilder {
  static async buildPatientScopeData(
    patient: WithRef<IPatient>,
    practice: IReffable<IPractice>,
    appUrl: string,
    marketingCampaignEntry?: WithRef<IMarketingCampaignEntry>
  ): Promise<IPatientScopeData> {
    const organisation = await snapshot(
      Brand.organisation$({ ref: Patient.brandRef({ ref: patient.ref }) })
    );
    const primaryContact = await Patient.resolvePrimaryContact(patient);
    const referrer = await this.resolveReferrer(patient);
    const patientForms = await Firestore.getDocs(
      undeletedQuery(Patient.formCol(patient))
    );
    return {
      patient,
      practice: await Firestore.getDoc(practice.ref),
      primaryContact,
      medicalHistoryLink: await generateMedicalHistoryUrl(patient, appUrl),
      patientFormsLink: await generatePatientFormsUrl(
        patient,
        practice,
        appUrl
      ),
      appointmentBookingLink: generateAppointmentBookingUrl(
        practice,
        appUrl,
        marketingCampaignEntry?.conversionUid
      ),
      unsubscribeLink: await generateUnsubscribeUrl(
        patient,
        practice,
        appUrl,
        marketingCampaignEntry
      ),
      patientForms,
      referrer,
      smsIntegration: organisation.integrations?.smsProvider,
      region: organisation.region,
    };
  }

  static async resolveReferrer(
    patient: WithRef<IPatient>
  ): Promise<undefined | Partial<IContactDetails>> {
    if (!patient.referrer) {
      return undefined;
    }
    if (isPatientReferrer(patient.referrer)) {
      const referrer = await Firestore.getDoc(patient.referrer.ref);
      return {
        name: referrer.name,
        email: referrer.email,
        address: referrer.address,
        jobTitle: undefined,
      };
    }
    if (isContactReferrer(patient.referrer)) {
      const referrer = await Firestore.getDoc(patient.referrer.ref);
      return {
        name: referrer.name,
        email: referrer.email,
        address: referrer.address,
        jobTitle: undefined,
      };
    }
    return {
      name: patient.referrer.name,
      email: undefined,
      address: undefined,
      jobTitle: undefined,
    };
  }

  static async buildPracticeScopeData(
    practice: WithRef<IPractice>
  ): Promise<IPracticeScopeData> {
    return Promise.resolve({ practice });
  }

  static async buildAppointmentScopeData(
    appointment: WithRef<IAppointment>,
    appUrl: string,
    marketingCampaignEntry?: WithRef<IMarketingCampaignEntry>
  ): Promise<IAppointmentScopeData> {
    const patientRef = Firestore.getParentDocRef<IPatient>(appointment.ref);
    const patient = await OrganisationCache.patients.getDoc(patientRef);
    const practice = await Firestore.getDoc(appointment.practice.ref);
    const patientScopeData = await this.buildPatientScopeData(
      patient,
      practice,
      appUrl,
      marketingCampaignEntry
    );
    const practiceScopeData = await this.buildPracticeScopeData(
      await Firestore.getDoc(appointment.practice.ref)
    );
    return {
      ...patientScopeData,
      ...practiceScopeData,
      appointment,
      practitioner: await Firestore.getDoc(appointment.practitioner.ref),
      confirmLink: await generateSelfConfirmUrl(
        patientRef,
        appointment,
        appUrl
      ),
    };
  }

  static async buildInvoiceScopeData(
    invoice: WithRef<IInvoice>,
    appUrl: string,
    marketingCampaignEntry?: WithRef<IMarketingCampaignEntry>
  ): Promise<IInvoiceScopeData> {
    const patientRef = Firestore.getParentDocRef<IPatient>(invoice.ref);
    const patient = await OrganisationCache.patients.getDoc(patientRef);
    const practice = await Firestore.getDoc(invoice.practice.ref);
    const patientScopeData = await this.buildPatientScopeData(
      patient,
      practice,
      appUrl,
      marketingCampaignEntry
    );
    return {
      ...patientScopeData,
      invoice,
      transactions: await Invoice.getTransactions(invoice),
      practice: await Firestore.getDoc(invoice.practice.ref),
      invoiceLink: await generateViewInvoiceUrl(invoice, appUrl),
    };
  }

  static async buildGapScopeData(
    gapCandidate: WithRef<ICandidateCalendarEvent>,
    appUrl: string,
    marketingCampaignEntry?: WithRef<IMarketingCampaignEntry>
  ): Promise<IGapScopeData | undefined> {
    const appointment = await Firestore.getDoc(
      gapCandidate.candidate.appointment
    );
    const appointmentScopedData = await this.buildAppointmentScopeData(
      appointment,
      appUrl,
      marketingCampaignEntry
    );
    if (!appointmentScopedData) {
      return;
    }

    return {
      ...appointmentScopedData,
      gapCandidate,
      gapOfferConfirmUrl: await generateGapOfferUrl(
        gapCandidate,
        appUrl,
        marketingCampaignEntry?.conversionUid
      ),
    };
  }

  static async buildTreatmentPlanScopeData(
    treatmentPlan: WithRef<ITreatmentPlan>,
    practice: WithRef<IPractice>,
    appUrl: string,
    marketingCampaignEntry?: WithRef<IMarketingCampaignEntry>
  ): Promise<ITreatmentPlanScopeData> {
    const patientRef = Firestore.getParentDocRef<IPatient>(treatmentPlan.ref);
    const patient = await OrganisationCache.patients.getDoc(patientRef);
    const patientScopeData = await this.buildPatientScopeData(
      patient,
      practice,
      appUrl,
      marketingCampaignEntry
    );
    return {
      ...patientScopeData,
      treatmentPlan,
    };
  }

  static async buildTreatmentStepScopeData(
    treatmentStep: WithRef<ITreatmentStep>,
    practice: WithRef<IPractice>,
    appUrl: string,
    marketingCampaignEntry?: WithRef<IMarketingCampaignEntry>
  ): Promise<ITreatmentStepScopeData> {
    const patientRef = Firestore.getParentDocRef<IPatient>(treatmentStep.ref);
    const treatmentPlan = await Firestore.getDoc(
      Firestore.getParentDocRef<ITreatmentPlan>(treatmentStep.ref)
    );
    const patient = await OrganisationCache.patients.getDoc(patientRef);
    const patientScopeData = await this.buildPatientScopeData(
      patient,
      practice,
      appUrl,
      marketingCampaignEntry
    );
    return {
      ...patientScopeData,
      treatmentStep,
      treatmentPlan,
    };
  }

  static async buildReferralScopeData(
    referral: WithRef<IReferral>,
    practice: IReffable<IPractice>,
    appUrl: string
  ): Promise<IReferralScopeData> {
    const patient = await OrganisationCache.patients.getDoc(
      referral.patient.ref
    );
    const patientScopeData = await this.buildPatientScopeData(
      patient,
      practice,
      appUrl
    );
    const referrerAddress = await this._resolveReferralActorAddress(
      (referral.referrer as ReferralActor | undefined) ?? undefined,
      patientScopeData.practice.address
    );
    const recipientAddress = await this._resolveReferralActorAddress(
      (referral.recipient as ReferralActor | undefined) ?? undefined,
      patientScopeData.practice.address
    );
    return { ...patientScopeData, referral, referrerAddress, recipientAddress };
  }

  private static async _resolveReferralActorAddress(
    actor: ReferralActor | undefined,
    practiceAddress: string
  ): Promise<string> {
    if (!actor) {
      return '';
    }
    if (isContactActor(actor)) {
      const contact = await Firestore.getDoc(actor.ref);
      return contact.address ?? '';
    }
    if (isStafferActor(actor)) {
      return practiceAddress;
    }
    return '';
  }
}
